import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader } from "../ui";
import { blankForm } from "./admin-usuarios/shared";
import { UserCreateForm } from "./admin-usuarios/UserCreateForm";
import { UserFilters } from "./admin-usuarios/UserFilters";
import { UserList } from "./admin-usuarios/UserList";
import { UserEditModal } from "./admin-usuarios/UserEditModal";


export default function AdminUsuarios() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("Todos");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited
  const [editForm, setEditForm] = useState(null);

  const load = () =>
    api
      .get("/admin/users")
      .then((r) => setList(r.data))
      .catch(() => toast.error("Erro ao carregar usuários internos."));

  useEffect(() => {
    load();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("A senha deve ter no mínimo 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        role: form.role,
        password: form.password,
      });
      toast.success("Usuário criado e e-mail de boas-vindas enviado.");
      setOpenCreate(false);
      setForm(blankForm());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      role: u.role,
      password: "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const patch = {};
    if (editForm.name !== editing.name) patch.name = editForm.name.trim();
    if (editForm.email !== editing.email) patch.email = editForm.email.trim();
    if ((editForm.phone || "") !== (editing.phone || ""))
      patch.phone = editForm.phone.trim();
    if (editForm.role !== editing.role) patch.role = editForm.role;
    if (editForm.password) patch.password = editForm.password;
    if (Object.keys(patch).length === 0) {
      toast.message("Nada a alterar.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${editing.id}`, patch);
      toast.success("Usuário atualizado.");
      setEditing(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "ativo" ? "inativo" : "ativo";
    if (next === "inativo" && !window.confirm(`Desativar ${u.name}?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { status: next });
      toast.success(`Status atualizado para ${next}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (
      !window.confirm(
        `Excluir definitivamente o usuário ${u.name}? Esta ação não pode ser desfeita.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Usuário excluído.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = list?.filter((u) => {
    if (filter === "Todos") return true;
    return u.role === filter;
  });

  return (
    <div data-testid="admin-usuarios">
      <PageHeader
        title="Conselheiros e Diretores"
        subtitle="Crie e gerencie acessos institucionais ao Portal ABLEMP. Esses usuários acessam arquivos enviados a todos, reuniões, fórum, eventos e estatuto."
        actions={
          <button
            data-testid="new-internal-user-btn"
            onClick={() => {
              setOpenCreate((v) => !v);
              setForm(blankForm());
            }}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Novo usuário
          </button>
        }
      />

      <UserCreateForm busy={busy} form={form} openCreate={openCreate} setForm={setForm} setOpenCreate={setOpenCreate} submitCreate={submitCreate} />
      <UserFilters filter={filter} filtered={filtered} list={list} setFilter={setFilter} />
      <UserList filtered={filtered} list={list} openEdit={openEdit} remove={remove} toggleStatus={toggleStatus} />
      <UserEditModal busy={busy} editForm={editForm} editing={editing} form={form} setEditForm={setEditForm} setEditing={setEditing} submitEdit={submitEdit} />
    </div>
  );
}
